import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, Rocket } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * Onboarding checklist for Talent + Employer dashboards.
 * Data-driven — pass the endpoint (`/professional/onboarding` or `/employer/onboarding`)
 * and an optional CTA link that jumps users to the right place to complete steps.
 */
export const OnboardingChecklist = ({ endpoint, ctaTo = "/professional/passport", ctaLabel = "Complete your profile", testId = "onboarding-widget" }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(endpoint)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [endpoint]);

    if (loading) {
        return (
            <div className="card-soft p-5" data-testid={testId}>
                <Loader2 size={16} className="animate-spin text-emerald-500" />
            </div>
        );
    }
    if (!data) return null;
    if (data.percent >= 100) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="card-soft p-5 border-emerald-500/30 bg-emerald-500/5"
                data-testid={testId}
            >
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <Rocket size={16} /> <span className="font-heading font-semibold">You&apos;re fully set up!</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Every checklist item is complete. Nice work.</p>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="card-soft p-5"
            data-testid={testId}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">Get set up</div>
                    <h3 className="mt-0.5 font-heading font-semibold text-foreground text-lg">
                        {data.completed} of {data.total} steps done
                    </h3>
                </div>
                <div className="text-right">
                    <div className="mono-score text-emerald-600 font-bold text-2xl">{data.percent}%</div>
                    <div className="text-[10px] text-muted-foreground">complete</div>
                </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${data.percent}%` }} />
            </div>
            <ul className="mt-4 space-y-1.5">
                {data.items.map((it) => (
                    <li key={it.key} className="flex items-start gap-2 text-sm" data-testid={`onboarding-item-${it.key}`}>
                        {it.done
                            ? <CheckCircle2 size={14} className="mt-0.5 text-emerald-500 shrink-0" />
                            : <Circle size={14} className="mt-0.5 text-muted-foreground shrink-0" />}
                        <span className={it.done ? "text-muted-foreground line-through" : "text-foreground"}>
                            {it.label}
                        </span>
                    </li>
                ))}
            </ul>
            <div className="mt-4">
                <Link to={ctaTo} className="btn-green text-sm inline-flex items-center gap-1" data-testid="onboarding-cta">
                    {ctaLabel}
                </Link>
            </div>
        </motion.div>
    );
};

export default OnboardingChecklist;
